import { useAppDispatch, useAppSelector } from 'hooks/redux.hooks';
//mui
import { Box, Stack, Button, Typography, Chip } from '@mui/material';
//components
import { CenteredNarrowContent } from 'theme/elements/Content';
import { FormCard } from 'theme/elements/Forms';
import { FormHeader1 } from 'theme/elements/Headers';
import { selectTranslations } from 'state/reducers/translation.reducers';
import { FormButton } from 'theme/elements/Buttons';
import { store } from 'state/store';
import { useEffect, useMemo, useState } from 'react';
import { getMessage } from 'state/reducers/balance.reducers';
import { userMessage } from 'constants/ui.constants';
import FormCreator from 'components/FormCreator';
import { field, fieldsGroupType, fieldType, validationTypes } from 'components/FormCreator/types';
import { validationDataType, withdrawalOptions } from 'constants/settings.constants';
import { getWithdrawalSuccess, setMessage, setWithdrawalSuccess } from 'state/reducers/balance.reducers';
import { sendWithdrawalRequest, getWithdrawalRequest } from 'services/balance.services';
import WarningDialog from 'components/WarningDialog';
import SuccessfulInquireMessage from 'components/SuccessfulInquireMessage';
import { SupportType } from 'constants/contact.constants';
import _ from 'lodash';
import { validateFields } from 'components/FormCreator/validation';
import { minimumNewBalanceAmount } from 'constants/balance.constants';
import { getCompanyBalances } from 'state/reducers/balance.reducers';
import { getExistingBankAccounts } from 'state/reducers/bank-account.reducers';
import { getFormattedNumber, getRawAmountInputValue } from 'utils';

export default function FundsWithdrawal() {
	const t = useAppSelector(selectTranslations);
	const dispatch = useAppDispatch();
	const userMessage: userMessage = useAppSelector(getMessage);
	const loading = useAppSelector((state) => state.balance.loading);
	//const [withdrawalOption, setWithdrawalOption] = useState(withdrawalOptions.REGULAR_WITHDRAWAL);
	const withdrawalSuccess = useAppSelector(getWithdrawalSuccess);
	const companyBalances = useAppSelector(getCompanyBalances);
	const existBankAccounts = useAppSelector(getExistingBankAccounts);
	const prefixFields = 'page.settings.funds_withdrawal.';
	const regularWithdrawalSubject = t(prefixFields + 'option_a');
	const regularWithdrawalMessage = t(prefixFields + 'message_a');
	const specialWithdrawalSubject = t(prefixFields + 'option_b');
	const specialWithdrawalMessage = t(prefixFields + 'message_b');

	const [initialValidation, setInitialValidation] = useState({});
	const fieldsValidations = {
		amount: [validationTypes.requierd, validationTypes.float, validationTypes.minAmount, validationTypes.maxAmount],
		withdrawalMessage: [validationTypes.requierd],
		currency: [validationTypes.requierd],
		companyBankAccountId: [validationTypes.requierd],
	};
	const [formValues, setFormValues] = useState({
		subject: regularWithdrawalSubject,
		withdrawalMessage: '',
		withdrawalType: String(withdrawalOptions.REGULAR_WITHDRAWAL),
		amount: '',
		currency: '',
		companyBankAccountId: '',
	});

	const currencies = useMemo(() => {
		return companyBalances.map((balance) => {
			return {
				amount: balance?.amount,
				symbol: balance?.currency?.symbol,
				value: balance?.currency?.code,
				label: balance?.currency?.name,
				disabled: false,
			};
		});
	}, [companyBalances]);

	const existBankAccountsOptions = useMemo(() => {
		return existBankAccounts.map((account) => {
			return {
				value: account.id,
				accountNumber: account.accountNumber,
				bankBranch: account.bankBranch,
				bankName: account?.bankName?.bankName,
				label: `${account?.bankName?.bankName}  ${account?.accountNumber}/${account?.bankBranch}`,
			};
		});
	}, [companyBalances]);

	const currenciesObj = useMemo(() => {
		return _.isArray(currencies) ? _.keyBy(currencies, 'value') : {};
	}, [currencies]);

	const validationData = useMemo((): validationDataType => {
		return {
			[validationTypes.minAmount]: minimumNewBalanceAmount,
			[validationTypes.maxAmount]: _.has(currenciesObj, formValues.currency)
				? _.get(currenciesObj, formValues.currency)?.amount
				: Infinity,
		} as validationDataType;
	}, [formValues]);

	const formFields = useMemo(() => {
		const isRegular = String(formValues.withdrawalType) !== String(withdrawalOptions.REGULAR_WITHDRAWAL);
		const amount = {
			type: fieldType.text,
			validation: [validationTypes.requierd],
			props: {
				disabled: _.isEmpty(formValues.currency),
				required: true,
				formatAsAmount: true,
				name: 'amount',
				label: t(`${prefixFields}amount.text`),
				value: _.get(formValues, 'amount', ''),
			},
		};
		const currency = {
			type: fieldType.select,
			validation: [validationTypes.requierd],
			props: {
				required: true,
				name: 'currency',
				valueProperty: 'id',
				label: t(`${prefixFields}currency.text`),
				value: _.get(formValues, 'currency', ''),
				options: _.values(currencies),
				sx: {
					display: 'inline-block',
					'& .MuiInputBase-root': {
						'&:before': {
							position: 'absolute',
							display: _.has(currenciesObj, formValues.currency) ? 'block' : 'none',
							content:
								`'${_.get(currenciesObj, formValues.currency)?.symbol}` +
								`${getFormattedNumber(_.get(currenciesObj, formValues.currency)?.amount)}'`,
							height: '24px',
							lineHeight: '24px',
							borderRadius: '16px',
							backgroundColor: 'rgba(145, 158, 171, 0.16)',
							padding: '0 8px',
							right: '60px',
						},
					},
				},
			},
			renderOption: (props, option) => {
				return (
					<Box
						component="li"
						disabled={true}
						{...props}
						className=""
						sx={{
							padding: '8px 16px',
							display: 'flex',
							justifyContent: 'space-between',
						}}
					>
						<Typography>{option.label}</Typography>
						<Chip size="small" label={`${option.symbol}${getFormattedNumber(option.amount)}`} />
					</Box>
				);
			},
		};
		const companyBankAccountId = {
			type: fieldType.select,
			validation: [validationTypes.requierd],
			props: {
				required: true,
				name: 'companyBankAccountId',
				valueProperty: 'id',
				label: t(`${prefixFields}companyBankAccountId.text`),
				value: _.get(formValues, 'companyBankAccountId', ''),
				options: existBankAccountsOptions,
			},
			renderOption: (props, option) => {
				return (
					<Box
						component="li"
						disabled={true}
						{...props}
						className=""
						sx={{
							padding: '8px 16px',
							display: 'flex',
							justifyContent: 'space-between',
						}}
					>
						<Typography>
							{option.accountNumber}/{option.bankBranch}
						</Typography>
						<Typography>{option?.bankName}</Typography>
					</Box>
				);
			},
		};
		const message = {
			type: fieldType.textArea,
			validation: [validationTypes.requierd],
			props: {
				required: true,
				name: 'withdrawalMessage',
				label: t(prefixFields + 'message_label.text'),
				value: _.get(formValues, 'withdrawalMessage', ''),
				placeholder: !isRegular ? regularWithdrawalMessage : specialWithdrawalMessage,
			},
		};

		let fields: field[] = [
			{
				type: fieldType.radio,
				props: {
					required: true,
					name: 'withdrawalType',
					value: _.get(formValues, 'withdrawalType', '') as string,
					options: [
						{
							value: String(withdrawalOptions.REGULAR_WITHDRAWAL),
							label: t(prefixFields + `option_a`),
						},
						{
							value: String(withdrawalOptions.CLOSE_WITHDRAWAL),
							label: t(prefixFields + `option_b`),
						},
					],
				},
			},
		];

		return _.concat(fields, isRegular ? message : [currency, amount, message, companyBankAccountId]);
	}, [formValues]);

	useEffect(() => {
		dispatch(setWithdrawalSuccess(false));
	}, []);

	const isValidForm = useMemo(() => {
		let obj = {};
		_.keys(formValues).map((key) => (obj[key] = String(formValues[key])));
		const invalidFields = validateFields(
			formValues.withdrawalType === withdrawalOptions.REGULAR_WITHDRAWAL
				? fieldsValidations
				: _.omit(fieldsValidations, ['amount', 'currency', 'companyBankAccountId']),
			obj,
			validationData
		);

		return _.isEmpty(invalidFields);
	}, [formValues]);

	const updateFormData = (event) => {
		const { value, name } = event.target;
		let obj = {};
		_.keys(formValues).map((key) => (obj[key] = String(formValues[key])));
		const invalidFields = validateFields(fieldsValidations, { [name]: _.trim(value) }, validationData);

		switch (event.target.name) {
			case 'withdrawalType':
				const isWithdrawal = value === withdrawalOptions.REGULAR_WITHDRAWAL;
				setFormValues({
					...formValues,
					subject: isWithdrawal ? regularWithdrawalSubject : specialWithdrawalSubject,
					withdrawalType: value,
				});
				break;
			case 'currency':
				setFormValues({
					...formValues,
					[name]: value,
					amount: '',
				});
				break;
			default:
				setFormValues({
					...formValues,
					[name]: value,
				});
		}
		setInitialValidation(
			_.isEmpty(invalidFields) ? _.omit(invalidFields, name) : { ...initialValidation, ...invalidFields }
		);
	};

	const handleSaveClick = () => {
		const { subject, withdrawalMessage, withdrawalType, amount, currency, companyBankAccountId } = formValues;
		dispatch(
			sendWithdrawalRequest(
				subject,
				withdrawalMessage,
				withdrawalType === withdrawalOptions.REGULAR_WITHDRAWAL
					? SupportType.Withdrawal
					: SupportType.WithdrawalWithClose,
				currency,
				Number(getRawAmountInputValue(amount)),
				Number(companyBankAccountId)
			)
		);
	};

	const openDialog = useMemo(() => {
		return userMessage.exist;
	}, [userMessage]);

	return (
		<Box>
			<WarningDialog
				open={openDialog}
				title={t(userMessage.text || '')}
				subTitle={userMessage.subText}
				handleCancel={() => {
					dispatch(setMessage({ exist: false }));
				}}
			/>
			<Stack>
				<CenteredNarrowContent>
					{
						<>
							{withdrawalSuccess && (
								<>
									<SuccessfulInquireMessage
										handleOnClick={() => {
											setFormValues({
												...formValues,
												withdrawalMessage: '',
												amount: '',
												currency: '',
											});
											dispatch(setWithdrawalSuccess(false));
										}}
										title={t(prefixFields + 'request_recieved.title')}
										subtitle={t(prefixFields + 'request_recieved.text')}
										buttonText={t(prefixFields + 'request_recieved.success_button.text')}
										imageKey={`${prefixFields} request_recieved.icon`}
									/>
								</>
							)}
						</>
					}

					{
						<>
							{!withdrawalSuccess && (
								<>
									<Box
										sx={{
											'& .customRadioWrap': { padding: '22px 16px 22px 12px', height: 'auto' },
										}}
									>
										<FormCard>
											<FormHeader1
												title={t(prefixFields + 'title')}
												text={t(prefixFields + 'text')}
											/>
											<FormCreator
												validations={fieldsValidations}
												initialValidation={initialValidation}
												name="withdrawalRequest"
												handleChange={updateFormData}
												fieldsGroups={[
													{
														type: fieldsGroupType.col,
														fields: formFields,
													},
												]}
											/>
										</FormCard>
									</Box>
									<FormButton
										sx={{
											width: '100%',
											display: 'flex',
											justifyContent: 'center',
											marginTop: '72px',
										}}
									>
										<Button
											disabled={loading || !isValidForm}
											variant="contained"
											onClick={handleSaveClick}
											sx={{ padding: '15px 48px', width: '70%' }}
										>
											{t(prefixFields + 'submit_request_button.text')}
										</Button>
									</FormButton>
								</>
							)}
						</>
					}
				</CenteredNarrowContent>
			</Stack>
		</Box>
	);
}
